// frontend/src/store/useLanguageStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../api/client';

// 🟢 Importamos los diccionarios físicos
import esJson from '../locales/es.json';
import enJson from '../locales/en.json';
import ptJson from '../locales/pt.json';

const dictionaries = {
  es: esJson,
  en: enJson,
  pt: ptJson
};

// 🟢 Helper para detectar el idioma nativo del navegador (Fail Fast)
const detectBrowserLanguage = () => {
  try {
    const browserLang = navigator.language || navigator.userLanguage;
    if (!browserLang) return 'en';

    const shortLang = browserLang.split('-')[0].toLowerCase();
    const supportedLangs = ['es', 'en', 'pt'];

    return supportedLangs.includes(shortLang) ? shortLang : 'en';
  } catch (error) {
    return 'en';
  }
};

export const useLanguageStore = create(
  persist(
    (set, get) => ({
      // 1. Estado inicial
      language: detectBrowserLanguage(),
      availableLanguages: [],

      // 2. Obtener idiomas desde la BD (Agnosticismo: la DB manda qué idiomas están activos)
      fetchLanguages: async () => {
        try {
          const { data } = await apiClient.get('/catalogs/languages');
          set({ availableLanguages: data });
        } catch (error) {
          console.error("Error loading languages from DB", error);
        }
      },

      // 3. Cambiar idioma dinámicamente
      setLanguage: (lang) => {
        if (!dictionaries[lang]) return;

        // Actualiza el HTML para SEO y traductores automáticos
        document.documentElement.lang = lang;

        set({ language: lang });
      },

      // 4. Función Traductora Agente (con Fallback ES + interpolación {{var}})
      t: (path, params = {}) => {
        if (!path) return '';
        const keys = path.split('.');
        const currentLang = get().language;
        let value = dictionaries[currentLang];

        // Intentamos buscar en el idioma actual
        for (const key of keys) {
          if (value === undefined) break;
          value = value[key];
        }

        // 🛡 FALLBACK: Si no existe en Inglés/Portugués, lo sacamos de Español
        if (value === undefined && currentLang !== 'es') {
          let fallbackValue = dictionaries['es'];
          for (const key of keys) {
            if (fallbackValue === undefined) break;
            fallbackValue = fallbackValue[key];
          }
          value = fallbackValue;
        }

        let resolved = value || path;

        // 🆕 Interpolación de parámetros: "Hola {{name}}" → "Hola Raul"
        // Soporta {{var}} con o sin espacios. Si una key del template no
        // tiene match en params, se deja literal (útil para debug).
        if (typeof resolved === 'string' && params && typeof params === 'object') {
          resolved = resolved.replace(/\{\{\s*([\w]+)\s*\}\}/g, (match, key) => {
            return params[key] !== undefined ? String(params[key]) : match;
          });
        }

        return resolved;
      }
    }),
    {
      name: 'vnext_lang_store', // Nombre limpio en LocalStorage
      // Hook que se ejecuta cuando Zustand termina de leer el LocalStorage al cargar la página
      onRehydrateStorage: () => (state) => {
        if (state && state.language) {
          document.documentElement.lang = state.language;
        }
      }
    }
  )
);